import {
    Component,
    OnInit,
    OnDestroy,
    ChangeDetectionStrategy,
} from '@angular/core';
import { Observable, takeUntil, tap } from 'rxjs';
import { FeesWaiversExemptionsHelperMessages } from './constants/fees-waivers-exemptions-messages';
import { ApplicationPageBaseComponent } from '../../../../shared/components/fcc-601/application-page-base/application-page-base.component';
import { FeesWaiversExemptionsRequest } from './models/fees-waivers-exemptions-request';
import { FeesWaiversExemptionsResponse } from './models/fees-waivers-exemptions-response';
import { BlockNavigationIfChange } from '../../../../core/models/block-navigation-if-change';
import { ActivatedRoute, Router } from '@angular/router';
import {
    FormControl,
    FormGroup,
    FormsModule,
    ReactiveFormsModule,
    Validators,
} from '@angular/forms';
import { StepValidationResult } from '../../shared/models/step-validation-result';
import { FormGroupType } from '../../../../core/models/form-group-type';
import { DirectEntryService } from '../shared/services/direct-entry/direct-entry.service';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { DropdownOption } from '../../../../core/models/dropdown-option';
import { DropdownService } from '../../../../core/services/resources/dropdown.service';
import { ApplicationFormFieldComponent } from '../../../../shared/components/fcc-601/application-form-field/application-form-field.component';
import { ApplicationPageFacadeService } from '../shared/services/application-page-facade/application-page-facade.service';
import { AttachmentGridComponent } from '../shared/components/attachment/attachment-grid/attachment-grid.component';
import { NgClass } from '@angular/common';
import { AttachmentRequiredButtonComponent } from '../shared/components/attachment/attachment-required-button/attachment-required-button.component';

@Component({
    selector: 'app-fees-waivers-exemptions',
    templateUrl: './fees-waivers-exemptions.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        MatSelectModule,
        MatFormFieldModule,
        MatInputModule,
        FormsModule,
        ReactiveFormsModule,
        MatTooltipModule,
        MatIconModule,
        ApplicationFormFieldComponent,
        AttachmentGridComponent,
        AttachmentRequiredButtonComponent,
        NgClass,
    ],
})
export class FeesWaiversExemptionsComponent
    extends ApplicationPageBaseComponent<
        FeesWaiversExemptionsRequest,
        FeesWaiversExemptionsResponse,
        FeesWaiversExemptionsResponse
    >
    implements OnInit, OnDestroy, BlockNavigationIfChange
{
    messages: typeof FeesWaiversExemptionsHelperMessages =
        FeesWaiversExemptionsHelperMessages;

    yesNoOptions: DropdownOption[] = [];

    constructor(
        protected override readonly route: ActivatedRoute,
        protected override readonly router: Router,
        protected override readonly applicationPageFacadeService: ApplicationPageFacadeService,
        private readonly directEntryService: DirectEntryService,
        private readonly dropdownService: DropdownService,
    ) {
        super(route, router, applicationPageFacadeService);
    }

    override ngOnInit(): void {
        super.ngOnInit();
        this.loadDropdownData();
    }

    override form: FormGroup<FormGroupType<FeesWaiversExemptionsResponse>> =
        new FormGroup<FormGroupType<FeesWaiversExemptionsResponse>>({
            applicationFeeExempt: new FormControl(
                undefined,
                Validators.required,
            ),
            regulatoryFeeExempt: new FormControl(
                undefined,
                Validators.required,
            ),
            requestingAWaiver: new FormControl(undefined, Validators.required),
            grandfatheredApprovedIntegrator: new FormControl(
                undefined,
                Validators.required,
            ),
        });

    private loadDropdownData() {
        this.dropdownService
            .getYesNoOptions()
            .pipe(
                takeUntil(this.destroy$),
                tap((options: DropdownOption[]) => {
                    this.yesNoOptions = options;
                }),
            )
            .subscribe();
    }

    protected override bindForm(response: FeesWaiversExemptionsResponse): void {
        this.form.patchValue(response);
    }

    protected override getFormDataFromApi(
        applicationId: string,
    ): Observable<FeesWaiversExemptionsResponse> {
        return this.directEntryService.getFeesWaiversExemptions(applicationId);
    }

    protected override saveFormDataToApi(
        request: FeesWaiversExemptionsRequest,
    ): Observable<StepValidationResult> {
        return this.directEntryService.saveFeesWaiversExemptions(request);
    }

    protected override generateRequestObject(): FeesWaiversExemptionsRequest {
        const formValue = this.form
            .value as Required<FeesWaiversExemptionsRequest>;

        return {
            ...formValue,
            applicationId: this.applicationId!,
        };
    }
}
